/**
 * The conversation as the browser sees it.
 *
 * A pure reducer over room events, deliberately separate from React: the rules
 * that decide whether a line is replaced, corrected or ignored are the ones
 * most worth testing, and they do not need a DOM to be exercised.
 *
 * Three rules carry the UI:
 *  - segments are keyed by `id`, so a revised partial replaces its predecessor
 *    rather than appending a second copy of the same sentence;
 *  - they are ordered by `seq`, which the store assigns, so a line that is
 *    finalised late keeps its place in the conversation;
 *  - a patch at or below the revision already held is dropped, so a late live
 *    draft cannot undo the end-of-session correction pass.
 */
import {
  LOW_CONFIDENCE_THRESHOLD,
  isEventOf,
  type Envelope,
  type Segment,
  type SessionStatus,
  type Speaker,
  type WorkerStatus,
} from '@lt/protocol';

export interface TranscriptState {
  sessionId: string | null;
  status: SessionStatus;
  workerStatus: WorkerStatus;
  /** When the meeting started, epoch ms, so a past meeting is dated by when it happened. */
  startedAt: number | null;
  /** Relay-assigned id for this browser. Needed to ignore our own echoes. */
  clientId: string | null;
  segments: Segment[];
  speakers: Map<string, Speaker>;
  /** Highest seq seen. Sent on reconnect so catch-up resumes exactly here. */
  lastSegmentSeq: number | null;
  viewerCount: number;
  /** True until the first `catchup` with `complete` arrives. */
  loading: boolean;
  error: string | null;
}

export const initialState: TranscriptState = {
  sessionId: null,
  status: 'starting',
  workerStatus: 'absent',
  startedAt: null,
  clientId: null,
  segments: [],
  speakers: new Map(),
  lastSegmentSeq: null,
  viewerCount: 0,
  loading: true,
  error: null,
};

export function isLowConfidence(segment: Segment): boolean {
  // The mic channel is "You" by construction and is never a guess.
  return segment.channel !== 'mic' && segment.confidence < LOW_CONFIDENCE_THRESHOLD;
}

export function speakerName(state: TranscriptState, speakerKey: string): string {
  return state.speakers.get(speakerKey)?.displayName ?? speakerKey;
}

/** Stable per-speaker colour index, so a renamed speaker keeps its colour. */
export function speakerColorIndex(speakerKey: string): number {
  if (speakerKey === 'you') return 0;
  const match = /^remote-(\d+)$/.exec(speakerKey);
  if (match) return Number(match[1]);
  let hash = 0;
  for (const ch of speakerKey) hash = (hash * 31 + ch.charCodeAt(0)) % 997;
  return 1 + (hash % 5);
}

/** Inserts or replaces by id, keeping the list ordered by seq. */
function upsert(segments: Segment[], incoming: Segment): Segment[] {
  const index = segments.findIndex((s) => s.id === incoming.id);
  if (index === -1) {
    // Appends are the common case — a live meeting arrives in order — so check
    // the tail before paying for a sort.
    const last = segments[segments.length - 1];
    if (!last || last.seq < incoming.seq) return [...segments, incoming];
    const next = [...segments, incoming];
    next.sort((a, b) => a.seq - b.seq);
    return next;
  }
  const existing = segments[index]!;
  if (incoming.revision < existing.revision) return segments;
  const next = [...segments];
  next[index] = { ...incoming, seq: existing.seq };
  return next;
}

export function reduce(state: TranscriptState, event: Envelope): TranscriptState {
  if (isEventOf(event, 'welcome')) {
    return {
      ...state,
      sessionId: event.payload.sessionId,
      clientId: event.payload.clientId,
      status: event.payload.status,
      workerStatus: event.payload.workerStatus,
      startedAt: event.payload.startedAt ?? state.startedAt,
      error: null,
    };
  }

  if (isEventOf(event, 'catchup')) {
    let segments = state.segments;
    for (const segment of event.payload.segments) segments = upsert(segments, segment);
    const speakers = new Map(state.speakers);
    for (const speaker of event.payload.speakers) speakers.set(speaker.key, speaker);
    return {
      ...state,
      segments,
      speakers,
      lastSegmentSeq: segments.length > 0 ? segments[segments.length - 1]!.seq : state.lastSegmentSeq,
      // Only the final page clears the spinner; a paged catch-up is still loading.
      loading: !event.payload.complete,
    };
  }

  if (isEventOf(event, 'transcript.segment')) {
    const segment = event.payload.segment;
    return {
      ...state,
      segments: upsert(state.segments, segment),
      lastSegmentSeq: Math.max(state.lastSegmentSeq ?? -1, segment.seq),
      loading: false,
    };
  }

  if (isEventOf(event, 'transcript.update')) {
    let segments = state.segments;
    for (const patch of event.payload.patches) {
      const index = segments.findIndex((s) => s.id === patch.id);
      if (index === -1) continue;
      const existing = segments[index]!;
      // The guard that keeps a stale correction from moving a line backwards.
      if (patch.revision <= existing.revision) continue;
      if (segments === state.segments) segments = [...segments];
      segments[index] = {
        ...existing,
        revision: patch.revision,
        text: patch.text ?? existing.text,
        speakerKey: patch.speakerKey ?? existing.speakerKey,
        confidence: patch.confidence ?? existing.confidence,
        isFinal: patch.isFinal ?? existing.isFinal,
      };
    }
    return segments === state.segments ? state : { ...state, segments };
  }

  if (isEventOf(event, 'speaker.rename')) {
    const speakers = new Map(state.speakers);
    const existing = speakers.get(event.payload.speakerKey);
    speakers.set(event.payload.speakerKey, {
      key: event.payload.speakerKey,
      displayName: event.payload.displayName,
      // A human name says nothing about which voice it is, so the diarizer's
      // confidence is preserved and the low-confidence marker stays honest.
      confidence: existing?.confidence ?? null,
    });
    return { ...state, speakers };
  }

  if (isEventOf(event, 'session.state')) {
    const speakers = new Map(state.speakers);
    for (const speaker of event.payload.speakers) {
      const existing = speakers.get(speaker.key);
      // A rename may not have reached the store yet; do not let a state
      // broadcast overwrite a name the user just set.
      speakers.set(speaker.key, existing?.displayName && existing.displayName !== speaker.key
        ? { ...speaker, displayName: existing.displayName }
        : speaker);
    }
    return {
      ...state,
      status: event.payload.status,
      workerStatus: event.payload.workerStatus,
      viewerCount: event.payload.viewerCount,
      speakers,
    };
  }

  if (isEventOf(event, 'session.end')) {
    return { ...state, status: 'ended', workerStatus: 'absent' };
  }

  if (isEventOf(event, 'error')) {
    // A refused handshake means no catch-up is coming, so stop promising one.
    const refused = ['unauthorized', 'session_not_found', 'session_ended'].includes(event.payload.code);
    return { ...state, error: event.payload.message, loading: refused ? false : state.loading };
  }

  return state;
}

/** Applies a run of events in order. Used by tests and by the catch-up path. */
export function reduceAll(state: TranscriptState, events: readonly Envelope[]): TranscriptState {
  return events.reduce(reduce, state);
}
